import type { GroupResponsibleState, GroupResponsibleStore } from '@/scopes/account/group-responsible/domain/types'
import { createStore } from 'zustand'
import { setIn } from '@/scopes/common/domain/functions/objectDeepOperations'
import { computeRoomSwapAssignments } from '../domain/roomSwap'
import { canSelectParticipant, getAssignedParticipantsForRoom } from '../pages/selectors'

const defaultInitialState: GroupResponsibleState = {
  bookingData: {
    group: {
      name: '',
      imgSrc: '',
      location: '',
      publicationCode: '',
      period: '',
      startingDate: '',
      subscriptionModel: 'Open',
      isBlocked: false,
    },
  },
  activeTab: 'participants',
  participants: {},
  rooms: {},
  buses: {},
  activeExtraInfoId: '',
  selectedParticipantIds: new Set(),
  selectedRoomParticipantIds: new Set(),
  isEditingParticipants: false,
  isEditingRoomAssignment: false,
  retrieveBookingData: {
    loading: true,
    error: false,
  },
  retrieveBusPointsData: {
    loading: false,
    error: false,
  },
  busPickupPoints: [],
  busReturnPoints: [],
  busAssignment: {
    selectedPickupPointId: '',
    selectedReturnPointId: '',
    sameAsPickup: true,
    submitting: false,
    error: false,
    pickupPointNotSelected: false,
    returnPointNotSelected: false,
  },
  roomAssignmentStatus: 'idle',
  stagedRoomAssignments: null,
}

export const groupResponsibleStore = createGroupResponsibleStore()

export function createGroupResponsibleStore(initialState: GroupResponsibleState = defaultInitialState) {
  return createStore<GroupResponsibleStore>(set => ({
    ...initialState,
    initialize: (state) => {
      set(() => state)
    },

    setIn: (path, value) => {
      set(state => setIn(state, path, value) as GroupResponsibleState)
    },

    changeTab: (tab) => {
      set(state => ({ ...state, activeTab: tab }))
    },

    changeExtraInfoId: (id) => {
      set(state => ({ ...state, activeExtraInfoId: id }))
    },

    toggleParticipantSelection: (participantId: string) => {
      set((state) => {
        const newSelectedIds = new Set(state.selectedParticipantIds)
        if (newSelectedIds.has(participantId)) {
          newSelectedIds.delete(participantId)
        }
        else {
          newSelectedIds.add(participantId)
        }
        return { ...state, selectedParticipantIds: newSelectedIds }
      })
    },

    selectAllParticipants: (participantIds: string[]) => {
      set(state => ({ ...state, selectedParticipantIds: new Set(participantIds) }))
    },

    clearAllSelections: () => {
      set(state => ({ ...state, selectedParticipantIds: new Set() }))
    },

    toggleIsEditingParticipants: (isEditing: boolean) => {
      set(state => ({ ...state, isEditingParticipants: isEditing }))
    },

    openRoomAssignment: (roomId: string) => {
      set((state) => {
        const assignedIds = getAssignedParticipantsForRoom({ ...state, activeExtraInfoId: roomId })
        return {
          ...state,
          activeExtraInfoId: roomId,
          isEditingRoomAssignment: true,
          selectedRoomParticipantIds: new Set(assignedIds),
        }
      })
    },

    toggleRoomParticipantSelection: (participantId: string) => {
      set((state) => {
        if (!canSelectParticipant(state, participantId)) {
          return state
        }
        const newSelectedIds = new Set(state.selectedRoomParticipantIds)
        if (newSelectedIds.has(participantId)) {
          newSelectedIds.delete(participantId)
        }
        else {
          newSelectedIds.add(participantId)
        }
        return { ...state, selectedRoomParticipantIds: newSelectedIds }
      })
    },

    closeRoomAssignment: () => {
      set(state => ({
        ...state,
        isEditingRoomAssignment: false,
        activeExtraInfoId: '',
        selectedRoomParticipantIds: new Set(),
        roomAssignmentStatus:
          state.roomAssignmentStatus === 'error' || state.roomAssignmentStatus === 'submitting'
            ? 'idle'
            : state.roomAssignmentStatus,
      }))
    },

    stageCurrentRoomAssignment: () => {
      set((state) => {
        const roomId = state.activeExtraInfoId
        if (!roomId) {
          return state
        }
        const selectedCustomerIds = Array.from(state.selectedRoomParticipantIds).map(Number)
        const updatedAssignments = computeRoomSwapAssignments(
          state.rooms,
          roomId,
          selectedCustomerIds,
          state.stagedRoomAssignments ?? undefined,
        )

        return {
          ...state,
          stagedRoomAssignments: updatedAssignments,
          isEditingRoomAssignment: false,
          activeExtraInfoId: '',
          selectedRoomParticipantIds: new Set(),
        }
      })
    },

    discardStagedRoomAssignments: () => {
      set(state => ({
        ...state,
        stagedRoomAssignments: null,
      }))
    },
  }))
}
